import dayjs from "dayjs";
import React, { useEffect } from "react";
import { Trans } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import useFindOrder from "@repo/shopify/hooks/useFindOrder";
import useOrderInfo from "@repo/shopify/hooks/useOrderInfo";
import useOrderPayMethod from "@repo/shopify/hooks/useOrderPayMethod";
import useTracking from "@repo/shopify/hooks/useTracking";
import { formatOrderAddress } from "@repo/shopify/utils/address";
import { extractId } from "@repo/shopify/utils/common";

import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import Address from "@molecules/Address";
import OrderLineItem from "@molecules/OrderLineItem";

import OrderPaymentInfo from "@organisms/OrderPaymentInfo";

import { URLS } from "@const/urls";

import useCartQuery from "@hooks/useCartQuery";
import useLanguage from "@hooks/useLanguage";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

import { formatAutoDiscountTitle } from "@utils/discount";

export default function OrderCompleted() {
  const navigate = useNavigate();
  const { orderId } = useParams();

  const { lang } = useLanguage();

  const { gtmPurchase } = useTracking();

  const { showFooter } = useLayoutStore();
  const { token, customer } = useCustomerStore();

  const { createCartInput } = useCartQuery();

  const { order, loading } = useFindOrder({ orderId, accessToken: token.accessToken, fetchFirst: 1 });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (loading || !orderId) return;

    if (!order) {
      navigate(URLS.NOT_FOUND, { replace: true });
      return;
    }

    gtmPurchase(order, extractId(customer.id), customer.email ?? undefined);
    showFooter(true);

    void createCartInput({
      variables: { input: { buyerIdentity: { customerAccessToken: token.accessToken }, lines: [] } },
    });
  }, [orderId, order, loading, token]);

  // 결제 수단 정보
  const paymentInfo = useOrderPayMethod({ lang, order });

  const {
    shippingAmount,
    taxAmount,
    dutyAmount,
    totalAmount,
    subTotalAmount,
    codeDiscountAmount,
    autoDiscountAmount,
    codeDiscountTitle,
    autoDiscountTitle,
    shippingTitle,
  } = useOrderInfo(order);

  // 배송지 정보
  const shippingAddress = formatOrderAddress({ address: order?.shippingAddress, lang });
  const billingAddress = formatOrderAddress({ address: order?.billingAddress, lang });

  const estimatedDeliveryRaw = order?.customAttributes.find((a) => a.key === "estimatedDelivery")?.value;
  const estimatedDeliveryDate = estimatedDeliveryRaw ? dayjs(estimatedDeliveryRaw).format("MMM DD, YYYY") : null;

  return (
    <>
      {!order ? (
        <Loading type="page" />
      ) : (
        <section className="pc:col-span-8 pc:col-start-3 col-span-8 grid grid-cols-subgrid gap-y-10">
          <section className="pc:col-span-8 pc:flex-col col-span-8 flex justify-between gap-5">
            <section className="flex flex-col gap-5 text-lg leading-none">
              <h2>Order Completed {order.name || ""}</h2>
            </section>
          </section>

          <section className="pc:col-span-10 pc:gap-y-0 col-span-8 grid grid-cols-subgrid gap-y-10">
            <section className="pc:col-span-3 col-span-8 col-start-1 flex flex-col gap-10 leading-none">
              <div className="pc:gap-2.5 flex flex-col gap-2">
                <span className="pc:leading-[145%] text-md leading-[130%]">
                  <Trans i18nKey="order_completed.description" />
                </span>
                <Link as="square" href={`${URLS.COLLECTIONS_DETAIL}/new`} variant="secondary">
                  Continue Shopping
                </Link>
              </div>
              <section className="pc:col-span-3 col-span-8 col-start-1 flex flex-col gap-5 leading-none">
                <div className="flex flex-col gap-y-1">
                  <span>Shipping Method</span>
                  <span className="font-normal">{shippingTitle}</span>
                </div>
                <div className="flex flex-col gap-y-1">
                  <span>Payment Detail</span>
                  <span className="font-normal">{paymentInfo}</span>
                </div>
                <div className="flex flex-col gap-y-1">
                  <span>Shipping Address</span>
                  <Address {...shippingAddress} className="font-normal" />
                </div>
                <div className="flex flex-col gap-y-1">
                  <span>Billing Address</span>
                  <Address {...billingAddress} className="font-normal" />
                </div>
                {order.fulfillmentStatus !== "FULFILLED" && estimatedDeliveryDate ? (
                  <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                    <span>Estimated Delivery Date</span>
                    <span className="font-normal">{estimatedDeliveryDate}</span>
                  </div>
                ) : null}
              </section>
            </section>

            <section className="pc:col-span-5 pc:col-start-4 col-span-8 grid h-fit grid-cols-subgrid gap-y-5 leading-none">
              {order?.lineItems.edges.length > 0 ? (
                <ul className="pc:col-span-5 col-span-8 grid grid-cols-subgrid gap-y-4">
                  {order?.lineItems.edges
                    ?.filter(({ node }) => !node.variant?.product.tags.includes("Gift"))
                    .map(({ node }) => {
                      if (!node.variant) return null;
                      return <OrderLineItem key={node.variant.id} node={node} />;
                    })}
                </ul>
              ) : null}

              <div className="pc:items-end pc:col-start-4 col-span-8 flex w-full flex-col">
                <div className="pc:w-screen-col-span-2 flex flex-col gap-1">
                  <OrderPaymentInfo
                    autoDiscountAmount={autoDiscountAmount}
                    autoDiscountTitle={formatAutoDiscountTitle(autoDiscountTitle)}
                    codeDiscountAmount={codeDiscountAmount}
                    codeDiscountTitle={codeDiscountTitle}
                    dutyAmount={dutyAmount}
                    originalTotalAmount={subTotalAmount}
                    shippingFee={shippingAmount}
                    taxAmount={taxAmount}
                    totalAmount={totalAmount}
                  />
                </div>
              </div>
            </section>
          </section>
        </section>
      )}
    </>
  );
}
